import React, { useEffect, useState, type ReactNode } from "react";
import type {
  AppContextState,
  GeneralAppContextType,
} from "../types/GeneralAppProvider";

export const THEMES_LS = "current_theme_ls";

export const THEMES = {
  LIGHT: "theme-light",
  DARK: "theme-dark",
} as const;

export const THEMES_NAMES = {
  LIGHT: "LIGHT",
  DARK: "DARK",
} as const;

export const GeneralAppContext = React.createContext({
  appClass: "",
  layoutClass: "",
  updateAppState: (changes: Partial<AppContextState>) => {},
  currentTheme: THEMES_NAMES.LIGHT,
} as unknown as GeneralAppContextType);

const removeAllClassesFromBody = () => {
  while (document.body.classList.length > 0) {
    document.body.classList.remove(document.body.classList.item(0)!);
  }
};

interface GeneralAppProviderProps {
  children: ReactNode;
}

const GeneralAppProvider: React.FC<GeneralAppProviderProps> = ({
  children,
}) => {
  const [appContext, setAppContext] = useState<AppContextState>({
    appClass: "",
    layoutClass: "",
    isFiltersToggleOpen: false,
    currentTheme: THEMES_NAMES.LIGHT,
  });

  const updateAppState = (state: Partial<AppContextState>) => {
    setAppContext({ ...appContext, ...state });
  };

  const updateTheme = (themeClass: string) => {
    removeAllClassesFromBody();
    document.body.classList.add(themeClass);

    const newTheme =
      themeClass === THEMES.DARK ? THEMES_NAMES.DARK : THEMES_NAMES.LIGHT;

    updateAppState({ layoutClass: themeClass, currentTheme: newTheme });
    localStorage.setItem(THEMES_LS, themeClass);

    return () => {
      removeAllClassesFromBody();
    };
  };

  useEffect(() => {
    const currentTheme = localStorage.getItem(THEMES_LS);
    updateTheme(currentTheme || THEMES.LIGHT);
  }, []);

  return (
    <GeneralAppContext.Provider
      value={{
        appContext,
        updateAppState,
        updateTheme,
      }}
    >
      {children}
    </GeneralAppContext.Provider>
  );
};

export default GeneralAppProvider;
